import { useState, useMemo, useCallback } from "react";
import { useData } from "../../contexts/DataContext";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Plus, MoreHorizontal, Pencil, Trash2, ListPlus, Check, CalendarIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Avatar, PriorityBadge, ProgressBar, isOverdue } from "../ui-bits";
import { cn } from "../../lib/utils";

function RowContent({ task, depth, subs, users, handlers, expanded, onToggle }) {
  const { onOpen, onEdit, onAddSubtask, onDelete, onToggleDone } = handlers;
  const doneSubs = subs.filter((s) => s.status === "done").length;
  const overdue = isOverdue(task);

  return (
    <div
      data-testid={`task-row-${task.id}`}
      className={cn("group flex items-center gap-2 h-10 border-b border-border hover:bg-muted/50 transition-colors cursor-pointer pr-2 hierarchy-indent", depth > 0 && "bg-muted/20")}
      style={{ "--d": depth }}
      onClick={() => onOpen(task)}
    >
      {subs.length > 0 ? (
        <button data-testid={`task-expand-${task.id}`} onClick={(e) => { e.stopPropagation(); onToggle(task.id); }}
          className="h-5 w-5 flex items-center justify-center text-muted-foreground hover:text-foreground shrink-0">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ) : (
        <span className="w-5 shrink-0" />
      )}

      <button data-testid={`task-toggle-done-${task.id}`} onClick={(e) => { e.stopPropagation(); onToggleDone(task); }}
        className={cn("h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
          task.status === "done" ? "bg-[#10B981] border-[#10B981] text-white" : "border-muted-foreground/50 hover:border-[#10B981]")}>
        {task.status === "done" && <Check size={10} strokeWidth={3} />}
      </button>

      <span className={cn("text-sm truncate flex-1 min-w-0", task.status === "done" && "line-through text-muted-foreground")}>{task.title}</span>

      {subs.length > 0 && (
        <span className="hidden sm:flex items-center gap-1.5 shrink-0 w-20" data-testid={`task-progress-${task.id}`}>
          <ProgressBar value={(doneSubs / subs.length) * 100} className="w-10" />
          <span className="text-[10px] text-muted-foreground">{doneSubs}/{subs.length}</span>
        </span>
      )}

      <span className="hidden md:block shrink-0"><PriorityBadge priority={task.priority} /></span>

      {task.due_date && (
        <span data-testid={`task-due-${task.id}`} className={cn("hidden lg:flex items-center gap-1 text-xs shrink-0", overdue ? "text-[#E31E24] font-semibold" : "text-muted-foreground")}>
          <CalendarIcon size={11} />{task.due_date.slice(5)}
        </span>
      )}

      <span className="flex items-center -space-x-1.5 shrink-0">
        {(task.assignees || []).slice(0, 3).map((id) => {
          const u = users.find((x) => x.id === id);
          return u ? <Avatar key={id} name={u.name} userId={u.id} size="sm" className="ring-2 ring-background" /> : null;
        })}
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`task-menu-${task.id}`} onClick={(e) => e.stopPropagation()}
            className="h-6 w-6 hidden group-hover:flex items-center justify-center rounded text-muted-foreground hover:text-foreground shrink-0">
            <MoreHorizontal size={14} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem data-testid={`task-edit-${task.id}`} onClick={(e) => { e.stopPropagation(); onEdit(task); }}><Pencil size={14} className="mr-2" />Edit</DropdownMenuItem>
          {depth < 5 && (
            <DropdownMenuItem data-testid={`task-add-subtask-${task.id}`} onClick={(e) => { e.stopPropagation(); onAddSubtask(task); }}><ListPlus size={14} className="mr-2" />Add subtask</DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid={`task-delete-${task.id}`} className="text-[#E31E24]" onClick={(e) => { e.stopPropagation(); onDelete(task); }}><Trash2 size={14} className="mr-2" />Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function flattenRows(task, allTasks, showSubtasks, collapsed, depth, out = []) {
  out.push({ task, depth });
  if (showSubtasks && !collapsed[task.id]) {
    for (const c of allTasks.filter((t) => t.parent_id === task.id)) {
      flattenRows(c, allTasks, showSubtasks, collapsed, depth + 1, out);
    }
  }
  return out;
}

export default function ListView({ groups, users, onOpenTask, onEditTask, onAddTask, onAddSubtask, showSubtasks = true }) {
  const { tasks, refreshTasks, removeTask } = useData();
  const [quickAdd, setQuickAdd] = useState(null);
  const [quickTitle, setQuickTitle] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const toggleRow = useCallback((id) => setCollapsed((p) => ({ ...p, [id]: !p[id] })), []);

  const toggleDone = useCallback(async (task) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: task.status === "done" ? "todo" : "done" });
      refreshTasks();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refreshTasks]);

  const deleteTask = useCallback(async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      removeTask(task.id);
      toast.success("Task deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  }, [removeTask]);

  const quickSubmit = useCallback(async (group) => {
    if (!quickTitle.trim()) { setQuickAdd(null); return; }
    try {
      await api.post("/tasks", {
        project_id: group.projectId, title: quickTitle.trim(),
        status: group.status || "todo", priority: "normal", assignees: [], due_date: null,
      });
      refreshTasks();
      setQuickTitle("");
      setQuickAdd(null);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [quickTitle, refreshTasks]);

  const handlers = useMemo(
    () => ({ onOpen: onOpenTask, onEdit: onEditTask, onAddSubtask, onDelete: deleteTask, onToggleDone: toggleDone }),
    [onOpenTask, onEditTask, onAddSubtask, deleteTask, toggleDone]
  );

  return (
    <div data-testid="list-view">
      {groups.map((group) => (
        <div key={group.key} className="mb-4">
          <div className="flex items-center gap-2 sticky top-0 bg-background z-[5] py-1.5 border-b border-border" data-testid={`group-header-${group.key}`}>
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: group.color }} />
            <span className="text-xs font-semibold uppercase tracking-[0.05em]">{group.label}</span>
            <span className="text-xs text-muted-foreground">{group.tasks.length}</span>
            <button data-testid={`add-task-${group.key}`} onClick={() => onAddTask(group)}
              className="ml-auto h-6 px-2 flex items-center gap-1 text-xs rounded-md text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors">
              <Plus size={13} />Add Task
            </button>
          </div>

          {group.tasks.flatMap((t) => flattenRows(t, tasks, showSubtasks, collapsed, 0)).map(({ task, depth }) => (
            <RowContent key={task.id} task={task} depth={depth}
              subs={showSubtasks ? tasks.filter((x) => x.parent_id === task.id) : []}
              users={users} handlers={handlers} expanded={!collapsed[task.id]} onToggle={toggleRow} />
          ))}

          {quickAdd === group.key ? (
            <div className="flex items-center gap-2 h-10 border-b border-border pl-8 pr-2 task-row-enter">
              <input autoFocus data-testid={`quick-add-input-${group.key}`} value={quickTitle} onChange={(e) => setQuickTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") quickSubmit(group); if (e.key === "Escape") setQuickAdd(null); }}
                onBlur={() => quickTitle.trim() && quickSubmit(group)}
                placeholder="Task name, press Enter to save"
                className="flex-1 h-7 px-2 text-sm rounded border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            </div>
          ) : (
            <button data-testid={`quick-add-${group.key}`} onClick={() => { setQuickAdd(group.key); setQuickTitle(""); }}
              className="flex items-center gap-2 h-9 pl-8 text-sm text-muted-foreground hover:text-[#E31E24] transition-colors w-full text-left">
              <Plus size={14} />Add Task
            </button>
          )}
        </div>
      ))}
      {groups.every((g) => g.tasks.length === 0) && (
        <div className="text-center py-16 text-muted-foreground" data-testid="list-empty-state">
          <p className="text-sm">No tasks match your filters.</p>
        </div>
      )}
    </div>
  );
}
